import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../services/authContext';
import {
  getEmployeeInvoices,
  getEmployeeInvoiceDetail,
  updateInvoiceState,
} from '../services/api/employeeInvoices';

export function useEmployeeInvoices() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['employeeInvoices'],
    queryFn: () => getEmployeeInvoices(),
    enabled: user?.employeeType?.name?.toLowerCase().includes('freelance') ?? false,
  });
}

export function useEmployeeInvoiceDetail(year: number, month: number) {
  return useQuery({
    queryKey: ['employeeInvoiceDetail', year, month],
    queryFn: () => getEmployeeInvoiceDetail(year, month),
    enabled: !!year && !!month,
  });
}

export function useUpdateInvoiceState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month, state }: { year: number; month: number; state: 'open' | 'sent' }) =>
      updateInvoiceState(year, month, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeInvoices'] });
      // MKS-125 — sending/reverting a month changes the FIFO deductions AND the
      // "earlier month still open" warning for EVERY other month, so invalidate
      // the whole detail family (prefix match), not just the mutated month.
      queryClient.invalidateQueries({ queryKey: ['employeeInvoiceDetail'] });
      // Sending/reverting an invoice persists/deletes extra cost applications
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCosts'] });
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCost'] });
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCostOverview'] });
      queryClient.invalidateQueries({ queryKey: ['myExtraCosts'] });
    },
  });
}
